import React from "react";
import { Icon } from "./registry";
import { localYmd } from "./lib/dates";
/* ============================================================
   Fence Works — TV Display (shop wall mode)
   A read-only, auto-rotating big-type dashboard for the shop TV:
   today's installs → sales leaderboard → pipeline & money. Rotates
   every 12s (click a dot to pin a panel), one tap for fullscreen.
   Everything computes live from the store — nothing to configure.
   ============================================================ */
const TV_money0 = window.VFP.money0;
const TV_WON = ["signed", "paid"];
const TV_OPEN = ["pending", "viewed"];
const TV_STAGES = [
  { id: "materials", label: "Order Supplies", color: "#f0a35e" },
  { id: "unscheduled", label: "Needs Scheduling", color: "#f5c451" },
  { id: "scheduled", label: "Scheduled", color: "#4aa8ff" },
  { id: "in_progress", label: "In Progress", color: "#8b7cf6" },
  { id: "completed", label: "Completed", color: "#34d399" },
];

function Clock() {
  const [now, setNow] = React.useState<any>(new Date());
  React.useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  return (
    <div style={{ textAlign: "right" }}>
      <div className="display tnum" style={{ fontSize: 30 }}>{now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</div>
      <div className="muted" style={{ fontSize: 14 }}>{now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
    </div>
  );
}

function TVDisplay({ store }: any) {
  const wrapRef = React.useRef<any>(null);
  const [panel, setPanel] = React.useState<any>(0);
  const [pinned, setPinned] = React.useState<any>(false);
  React.useEffect(() => {
    if (pinned) return;
    const t = setInterval(() => setPanel(p => (p + 1) % 3), 12000);
    return () => clearInterval(t);
  }, [pinned]);
  const fullscreen = () => { const el = wrapRef.current; if (!el) return; if (document.fullscreenElement) document.exitFullscreen(); else el.requestFullscreen && el.requestFullscreen(); };

  const orders = store.workOrders || [], quotes = store.quotes || [], crews = (store.settings && store.settings.crews) || [];
  const today = localYmd();
  const todays = orders.filter(o => o.scheduledStart && o.status !== "completed" && o.scheduledStart <= today && (o.scheduledEnd || o.scheduledStart) >= today);
  const upcoming = orders.filter(o => o.scheduledStart && o.scheduledStart > today && o.status !== "completed")
    .sort((a, b) => a.scheduledStart.localeCompare(b.scheduledStart)).slice(0, 4);
  const m0 = new Date(); const from = new Date(m0.getFullYear(), m0.getMonth(), 1).getTime();
  const monthQ = quotes.filter(q => (q.createdAt || 0) >= from);
  const reps: any = {};
  monthQ.forEach(q => { if (!TV_WON.includes(q.status)) return; const k = (q.rep || "").trim() || "Unassigned"; reps[k] = (reps[k] || 0) + (q.total || 0); });
  const board = Object.entries(reps).map(([name, rev]) => ({ name, rev })).sort((a: any, b: any) => b.rev - a.rev).slice(0, 5);
  const pipeline$ = quotes.filter(q => TV_OPEN.includes(q.status)).reduce((a, q) => a + (q.total || 0), 0);
  const wonMo$ = monthQ.filter(q => TV_WON.includes(q.status)).reduce((a, q) => a + (q.total || 0), 0);
  const medal = ["#f5c451", "#c0c8d4", "#d19a6b"];
  const crewOf = (id) => crews.find(c => c.id === id);

  const PANELS = ["Today's Installs", "Sales Leaderboard", "Pipeline & Money"];
  return (
    <div className="page fade-up" ref={wrapRef} style={{ background: "var(--bg, #090d14)", minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <div className="row between" style={{ marginBottom: 22, alignItems: "flex-start" }}>
        <div>
          <h1 className="display" style={{ fontSize: 34, margin: 0 }}>{(store.company && store.company.name) || (store.settings && store.settings.companyName) || "FenceWorks"}</h1>
          <div className="row gap-8" style={{ marginTop: 8 }}>
            {PANELS.map((p, i) => (
              <button key={p} onClick={() => { setPanel(i); setPinned(true); }} title={p}
                style={{ width: i === panel ? 26 : 10, height: 10, borderRadius: 99, border: 0, cursor: "pointer", transition: ".3s", background: i === panel ? "var(--teal)" : "var(--surface-3)" }} />
            ))}
            <span className="muted" style={{ fontSize: 13, marginLeft: 6 }}>{PANELS[panel]}{pinned ? " · pinned" : ""}</span>
            {pinned && <button className="btn btn-ghost btn-sm" onClick={() => setPinned(false)}>Resume rotation</button>}
          </div>
        </div>
        <div className="row gap-12" style={{ alignItems: "flex-start" }}>
          <Clock />
          <button className="btn btn-ghost btn-sm" onClick={fullscreen} title="Fullscreen for the shop TV"><Icon name="chevright" size={14} style={{ transform: "rotate(-45deg)" }} />Fullscreen</button>
        </div>
      </div>

      {panel === 0 && (
        <div style={{ flex: 1 }}>
          <div className="kicker" style={{ fontSize: 13, marginBottom: 14 }}>On site today · {todays.length}</div>
          {todays.length === 0
            ? <div className="card pad" style={{ textAlign: "center", padding: 60 }}><div className="display" style={{ fontSize: 28, color: "var(--fg-3)" }}>No installs on the board today</div></div>
            : <div style={{ display: "grid", gap: 14 }}>
              {todays.map(o => { const cr = crewOf(o.crewId); return (
                <div key={o.id} className="card pad row between" style={{ padding: "20px 24px", flexWrap: "wrap", gap: 10 }}>
                  <div className="row gap-12" style={{ minWidth: 0 }}>
                    <span style={{ width: 14, height: 14, borderRadius: 4, background: (cr && cr.color) || "var(--teal)", flexShrink: 0, marginTop: 8 }} />
                    <div style={{ minWidth: 0 }}>
                      <div className="display" style={{ fontSize: 24, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.customer || o.title}</div>
                      <div className="muted" style={{ fontSize: 15 }}>{o.address}{cr ? " · " + cr.name + (cr.foreman ? " (" + cr.foreman + ")" : "") : ""}</div>
                    </div>
                  </div>
                  <div className="display tnum" style={{ fontSize: 24, color: "var(--teal)" }}>{TV_money0(o.total || 0)}</div>
                </div>
              ); })}
            </div>}
          {upcoming.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div className="kicker" style={{ fontSize: 12, marginBottom: 10 }}>Up next</div>
              <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                {upcoming.map(o => <span key={o.id} className="chip" style={{ fontSize: 13, padding: "6px 12px" }}>{new Date(o.scheduledStart + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })} · {o.customer || o.title}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {panel === 1 && (
        <div style={{ flex: 1 }}>
          <div className="kicker" style={{ fontSize: 13, marginBottom: 14 }}>Revenue won · {new Date().toLocaleDateString("en-US", { month: "long" })}</div>
          {board.length === 0
            ? <div className="card pad" style={{ textAlign: "center", padding: 60 }}><div className="display" style={{ fontSize: 28, color: "var(--fg-3)" }}>No wins yet this month — go get one 🔔</div></div>
            : <div style={{ display: "grid", gap: 14 }}>
              {board.map((r: any, i) => (
                <div key={r.name} className="card pad row between" style={{ padding: "18px 24px" }}>
                  <div className="row gap-14" style={{ alignItems: "center" }}>
                    <span style={{ display: "grid", placeItems: "center", width: 40, height: 40, borderRadius: 99, fontWeight: 900, fontSize: 18, color: i < 3 ? "#06121a" : "var(--fg-3)", background: i < 3 ? medal[i] : "var(--surface-3)" }}>{i + 1}</span>
                    <span className="display" style={{ fontSize: 26 }}>{r.name}</span>
                  </div>
                  <span className="display tnum" style={{ fontSize: 26, color: "var(--teal)" }}>{TV_money0(r.rev)}</span>
                </div>
              ))}
            </div>}
        </div>
      )}

      {panel === 2 && (
        <div style={{ flex: 1 }}>
          <div className="kicker" style={{ fontSize: 13, marginBottom: 14 }}>Pipeline &amp; money</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 16 }}>
            {[["Won this month", TV_money0(wonMo$), "var(--teal)"], ["Open pipeline", TV_money0(pipeline$), "var(--violet)"],
              ["Jobs in flight", orders.filter(o => o.status !== "completed").length, "var(--cyan)"],
              ["Unpaid completions", orders.filter(o => { if (o.status !== "completed" || !o.quoteId) return false; const q = quotes.find(x => x.id === o.quoteId); if (!q) return false; const paid = (Number(q.depositPaid) || 0) + (Array.isArray(q.payments) ? q.payments : []).reduce((a, p) => a + (Number(p.amount) || 0), 0); return (q.total || 0) - paid > 0.5; }).length, "var(--red)"]].map(([lb, v, col]: any) => (
              <div key={lb} className="card pad" style={{ padding: "22px 24px" }}>
                <div className="kicker" style={{ marginBottom: 8 }}>{lb}</div>
                <div className="display tnum" style={{ fontSize: 34, color: col }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="row gap-10" style={{ flexWrap: "wrap" }}>
            {TV_STAGES.map(s => {
              const n = orders.filter(o => o.status === s.id).length;
              return <span key={s.id} className="chip" style={{ fontSize: 15, padding: "10px 16px" }}><span style={{ width: 10, height: 10, borderRadius: 99, background: s.color, display: "inline-block" }} /> <b className="tnum">{n}</b>&nbsp;{s.label}</span>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

Object.assign(window, { TVDisplay });

export { TVDisplay };
